import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, UserPlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function AuthPage() {
  const { user, loading, login, register, loginWithGoogle } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ displayName: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && user) {
      navigate(location.state?.from || "/compte", { replace: true });
    }
  }, [loading, user, navigate, location.state]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "register") {
        await register(form);
      } else {
        await login(form);
      }
      navigate(location.state?.from || "/compte", { replace: true });
    } catch (err) {
      setError(
        err.code === "auth/email-already-in-use"
          ? "Cet email a deja un compte."
          : "Connexion impossible. Verifie tes informations."
      );
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setError("");
    setBusy(true);
    try {
      await loginWithGoogle();
      navigate(location.state?.from || "/compte", { replace: true });
    } catch {
      setError("Connexion Google annulee ou impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-black px-6 py-24 text-white md:px-20">
      <section className="mx-auto grid max-w-6xl grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div>
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
            Compte Unlimited
          </p>
          <h1 className="text-5xl font-black uppercase leading-none md:text-7xl">
            Connecte-toi avant de reserver ou commander.
          </h1>
          <p className="readable-copy mt-6 max-w-xl text-lg text-gray-300">
            Ton espace regroupe tes reservations studio, commandes services,
            achats Latdior et statuts admin.
          </p>
        </div>

        <div className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-8">
          <div className="mb-6 grid grid-cols-2 rounded-full border border-white/10 bg-black p-1">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`rounded-full px-4 py-2 text-sm font-bold uppercase ${
                mode === "login" ? "bg-white text-black" : "text-gray-400"
              }`}
            >
              Connexion
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              className={`rounded-full px-4 py-2 text-sm font-bold uppercase ${
                mode === "register" ? "bg-white text-black" : "text-gray-400"
              }`}
            >
              Inscription
            </button>
          </div>

          {error && (
            <p className="mb-4 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </p>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-400">
                  Nom
                </span>
                <input
                  value={form.displayName}
                  onChange={(e) => setForm((current) => ({ ...current, displayName: e.target.value }))}
                  className="w-full rounded border border-white/10 bg-black px-4 py-3 outline-none focus:border-red-500"
                  placeholder="Nom artiste ou prenom"
                />
              </label>
            )}
            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-400">
                Email
              </span>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))}
                className="w-full rounded border border-white/10 bg-black px-4 py-3 outline-none focus:border-red-500"
                placeholder="ton@email.com"
              />
            </label>
            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-400">
                Mot de passe
              </span>
              <input
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm((current) => ({ ...current, password: e.target.value }))}
                className="w-full rounded border border-white/10 bg-black px-4 py-3 outline-none focus:border-red-500"
                placeholder="6 caracteres minimum"
              />
            </label>

            <button
              disabled={busy}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black transition hover:bg-red-600 hover:text-white disabled:opacity-60"
            >
              {mode === "register" ? <UserPlus size={18} /> : <Mail size={18} />}
              {busy ? "Traitement..." : mode === "register" ? "Creer mon compte" : "Se connecter"}
            </button>
          </form>

          <button
            type="button"
            onClick={google}
            disabled={busy}
            className="mt-4 w-full rounded-full border border-white/15 px-5 py-3 text-sm font-bold uppercase transition hover:border-white hover:bg-white hover:text-black disabled:opacity-60"
          >
            Continuer avec Google
          </button>

          <p className="mt-6 text-center text-sm text-gray-500">
            Retourner a{" "}
            <Link to="/" className="text-white hover:text-red-500">
              l'accueil
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
}
